import { AlertCircle, CheckCircle, FileText, Loader2, X, XCircle } from "lucide-react";
import { Button } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import { formatCsvIssue } from "./utils";
import type { UploadedFile } from "./useMedicationUploadForm";

/** How many individual errors or warnings are listed before collapsing into a "… and N more" line. */
const MAX_LISTED_ISSUES = 3;

export interface MedicationUploadFileRowProps {
  uploadedFile: UploadedFile;
  onRemove: (id: string) => void;
}

export const MedicationUploadFileRow = ({ uploadedFile, onRemove }: MedicationUploadFileRowProps) => {
  const { t } = useTranslation("contacts");

  const { validationResult, isValidating } = uploadedFile;
  const errors = validationResult?.errors ?? [];
  const warnings = validationResult?.warnings ?? [];
  const validRowCount = validationResult?.data.length ?? 0;

  const formatIssue = (issue: (typeof errors)[number]) =>
    formatCsvIssue(issue, (row, message) => t("Row {{row}}: {{message}}", { row, message }));

  return (
    <div className="rounded-lg border">
      <div className="flex items-start gap-3 p-3">
        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-medium">{uploadedFile.file.name}</p>
            {isValidating && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            {!isValidating && validationResult && (
              <>
                {validationResult.isValid && warnings.length === 0 && (
                  <CheckCircle className="h-4 w-4 shrink-0 text-green-600" />
                )}
                {validationResult.isValid && warnings.length > 0 && (
                  <AlertCircle className="h-4 w-4 shrink-0 text-yellow-600" />
                )}
                {!validationResult.isValid && <XCircle className="h-4 w-4 shrink-0 text-destructive" />}
              </>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {(uploadedFile.file.size / 1024 / 1024).toFixed(2)} MB
            {validationResult && !isValidating && t(" • {{count}} valid row", { count: validRowCount })}
          </p>
        </div>
        <Button
          variant="ghost"
          color="muted"
          size="sm"
          iconOnly
          hoverLabel={t("Remove")}
          onClick={() => onRemove(uploadedFile.id)}
        >
          <X />
        </Button>
      </div>

      {!isValidating && validationResult && (errors.length > 0 || warnings.length > 0) && (
        <div className="space-y-2 border-t bg-muted/30 px-3 py-2">
          {errors.length > 0 && (
            <div className="space-y-1">
              <p className="flex items-center gap-1 text-xs font-semibold text-destructive">
                <XCircle className="h-3 w-3" />
                {t("Errors ({{amount}})", { amount: errors.length })}
              </p>
              {errors.slice(0, MAX_LISTED_ISSUES).map((error, idx) => (
                <p key={idx} className="ml-4 text-xs text-destructive">
                  {formatIssue(error)}
                </p>
              ))}
              {errors.length > MAX_LISTED_ISSUES && (
                <p className="ml-4 text-xs italic text-destructive">
                  {t("... and {{count}} more errors", { count: errors.length - MAX_LISTED_ISSUES })}
                </p>
              )}
            </div>
          )}
          {warnings.length > 0 && (
            <div className="space-y-1">
              <p className="flex items-center gap-1 text-xs font-semibold text-yellow-700">
                <AlertCircle className="h-3 w-3" />
                {t("Warnings ({{amount}})", { amount: warnings.length })}
              </p>
              {warnings.slice(0, MAX_LISTED_ISSUES).map((warning, idx) => (
                <p key={idx} className="ml-4 text-xs text-yellow-600">
                  {formatIssue(warning)}
                </p>
              ))}
              {warnings.length > MAX_LISTED_ISSUES && (
                <p className="ml-4 text-xs italic text-yellow-600">
                  {t("... and {{count}} more warnings", { count: warnings.length - MAX_LISTED_ISSUES })}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
